import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import UniversalLivestockPage from "@/pages/UniversalLivestockPage";
import SpeciesListPage from "@/pages/SpeciesListPage";
import SpeciesDetailPage from "@/pages/SpeciesDetailPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import ReportsPage from "@/pages/ReportsPage";
import FinancialPage from "@/pages/FinancialPage";
import WorkforceAnalyticsPage from "@/pages/WorkforceAnalyticsPage";
import RewardsPage from "@/pages/RewardsPage";
import RewardsControlPage from "@/pages/RewardsControlPage";
import { rewardsEnabled } from "@/lib/rewards";
import InventoryPage from "@/pages/InventoryPage";
import FeedInventoryPage from "@/pages/FeedInventoryPage";
import PurchasingPage from "@/pages/PurchasingPage";
import SalesPage from "@/pages/SalesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SalesTargetsPage from "@/pages/SalesTargetsPage";
import BreakEvenPage from "@/pages/BreakEvenPage";
import EmployeesPage from "@/pages/EmployeesPage";
import TasksPage from "@/pages/TasksPage";
import CalendarPage from "@/pages/CalendarPage";
import ScanPage from "@/pages/ScanPage";
import AssistantPage from "@/pages/AssistantPage";
import WorkerPage from "@/pages/WorkerPage";
import SalesInventoryPage from "@/pages/SalesInventoryPage";
import PosPage from "@/pages/PosPage";
import CustomersPage from "@/pages/CustomersPage";
import ShiftsPage from "@/pages/ShiftsPage";
import DeliveriesPage from "@/pages/DeliveriesPage";
import GrowthPage from "@/pages/GrowthPage";
import SalesDashboardPage from "@/pages/SalesDashboardPage";
import BatchesPage from "@/pages/BatchesPage";
import FieldModePage from "@/pages/FieldModePage";
import ConstructionPage from "@/pages/ConstructionPage";
import AnimalProfilePage from "@/pages/AnimalProfilePage";
import BirthToSalePage from "@/pages/BirthToSalePage";
import AlertsPage from "@/pages/AlertsPage";
import CropsPage from "@/pages/CropsPage";
import HRPage from "@/pages/HRPage";
import PayrollPage from "@/pages/PayrollPage";
import AquaculturePage from "@/pages/AquaculturePage";
import FormsPage from "@/pages/FormsPage";
import FleetPage from "@/pages/FleetPage";
import EquipmentPage from "@/pages/EquipmentPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import IncidentsPage from "@/pages/IncidentsPage";
import AttendancePayrollPage from "@/pages/AttendancePayrollPage";
import PendingApprovalScreen from "@/components/PendingApprovalScreen";
import ErrorBoundary from "@/components/ErrorBoundary";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import EmployeeShell from "@/components/employee/EmployeeShell";
import EmployeeHomePage from "@/pages/employee/EmployeeHomePage";
import EmployeeAddSheetPage from "@/pages/employee/EmployeeAddSheetPage";
import EmployeeAlertsPage from "@/pages/employee/EmployeeAlertsPage";
import EmployeeProfilePage from "@/pages/employee/EmployeeProfilePage";
import EmployeePayrollPage from "@/pages/employee/EmployeePayrollPage";
import AttendanceCalendarPage from "@/pages/AttendanceCalendarPage";
import { canAccessDashboardPath, canAccessPersonalTasks, canEnterFieldMode, getFieldModeRouteForUser, getHomeRouteForUser, isDashboardUser, normalizedRole } from "@/lib/routeHome";
import { useTranslation } from "react-i18next";
import SyncMonitorPage from "@/pages/SyncMonitorPage";
import OfflineCapturePage from "@/pages/employee/OfflineCapturePage";
import RecycleBinPage from "@/pages/RecycleBinPage";

const AI_ENABLED = process.env.REACT_APP_AI_ENABLED !== "0";

function Protected({ children, perm, owner, strictOwner, shell }) {
    const { user, can, isOwner } = useAuth();
    const loc = useLocation();
    const { t } = useTranslation();
    if (user === null) {
        return (
            <div className="min-h-screen grid place-items-center bg-background">
                <div className="tiny-label animate-pulse">{t("common.loading_farm")}</div>
            </div>
        );
    }
    if (!user) {
        const next = encodeURIComponent(`${loc.pathname}${loc.search}`);
        return <Navigate to={`/login?next=${next}`} replace />;
    }
    // Approval gate — any non-owner user must be explicitly approved by the Owner.
    if (!isOwner() && user.approved === false) {
        return <PendingApprovalScreen />;
    }
    if (shell === "owner" && !isDashboardUser(user)) return <Navigate to="/field" replace />;
    if (shell === "field" && normalizedRole(user) === "buyer") return (
        <div className="p-8">
            <h1 className="text-2xl font-bold">{t("access.denied")}</h1>
            <p className="text-muted-foreground mt-2">{t("access.buyer_restricted")}</p>
        </div>
    );
    if (shell === "tasks" && !canAccessPersonalTasks(user)) return (
        <div className="p-8">
            <h1 className="text-2xl font-bold">{t("access.denied")}</h1>
            <p className="text-muted-foreground mt-2">{t("access.tasks_restricted")}</p>
        </div>
    );
    if (shell === "owner" && !canAccessDashboardPath(user, loc.pathname)) return (
        <div className="p-8">
            <h1 className="text-2xl font-bold">{t("access.denied")}</h1>
            <p className="text-muted-foreground mt-2">{t("access.area_restricted")}</p>
        </div>
    );
    if (owner && !isOwner()) return (
        <div className="p-8">
            <h1 className="text-2xl font-bold">{t("access.owner_only")}</h1>
            <p className="text-muted-foreground mt-2">{t("access.owner_restricted")}</p>
        </div>
    );
    // Fail closed before the owner-only child mounts. Redirect denied users to
    // their own known-good landing page so no Recycle Bin request is issued and
    // they cannot become stranded in the legacy Overview loading state.
    if (strictOwner && (user?.role_name || "").trim().toLowerCase() !== "owner") {
        return <Navigate to={getHomeRouteForUser(user)} replace />;
    }
    if (perm && !can(perm)) return (
        <div className="p-8">
            <h1 className="text-2xl font-bold">{t("access.denied")}</h1>
            <p className="text-muted-foreground mt-2">{t("access.no_permission")}</p>
        </div>
    );
    return children;
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ErrorBoundary>
                        <PWAInstallPrompt />
                        <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/" element={<Protected><RoleLanding /></Protected>} />
                        <Route path="/field/*" element={<Protected shell="field"><FieldModeRoute /></Protected>}>
                            <Route index element={<EmployeeHomePage />} />
                            <Route path="tasks" element={<WorkerPage />} />
                            <Route path="add" element={<EmployeeAddSheetPage />} />
                            <Route path="capture" element={<OfflineCapturePage />} />
                            <Route path="livestock" element={<FieldModePage />} />
                            <Route path="alerts" element={<EmployeeAlertsPage />} />
                            <Route path="profile" element={<EmployeeProfilePage />} />
                            <Route path="attendance" element={<AttendanceCalendarPage />} />
                            <Route path="payroll" element={<EmployeePayrollPage />} />
                            {rewardsEnabled() && <Route path="rewards" element={<RewardsPage />} />}
                            {AI_ENABLED && <Route path="ai-katulong" element={<AssistantPage />} />}
                            <Route path="*" element={<Navigate to="/field" replace />} />
                        </Route>
                        <Route path="/worker" element={<Protected shell="tasks"><WorkerPage /></Protected>} />
                        <Route element={<Protected shell="owner"><AppLayout /></Protected>}>
                            <Route index element={<RoleLanding />} />
                            <Route path="/dashboard" element={<DashboardPage />} />
                            <Route path="/livestock" element={<Protected perm="view"><UniversalLivestockPage /></Protected>} />
                            <Route path="/species" element={<SpeciesListPage />} />
                            <Route path="/species/:id" element={<SpeciesDetailPage />} />
                            <Route path="/reports" element={<Protected perm="manage_reports"><ReportsPage /></Protected>} />
                            <Route path="/financial" element={<Protected perm="manage_finance"><FinancialPage /></Protected>} />
                            <Route path="/workforce-analytics" element={<Protected owner><WorkforceAnalyticsPage /></Protected>} />
                            {rewardsEnabled() && <Route path="/rewards/*" element={<Protected owner><RewardsControlPage /></Protected>} />}
                            <Route path="/sales-targets" element={<Protected perm="manage_finance"><SalesTargetsPage /></Protected>} />
                            <Route path="/break-even" element={<Protected perm="manage_finance"><BreakEvenPage /></Protected>} />
                            <Route path="/employees" element={<Protected owner><EmployeesPage /></Protected>} />
                            <Route path="/attendance" element={<Protected owner><AttendancePayrollPage /></Protected>} />
                            <Route path="/attendance/calendar" element={<Protected owner><AttendanceCalendarPage /></Protected>} />
                            <Route path="/tasks" element={<Protected perm="add"><TasksPage /></Protected>} />
                            <Route path="/calendar" element={<CalendarPage />} />
                            <Route path="/field-mode" element={<FieldModeDashboardRoute />} />
                            <Route path="/scan" element={<ScanPage />} />
                            {AI_ENABLED && <Route path="/ai-katulong" element={<AssistantPage />} />}
                            {AI_ENABLED && <Route path="/assistant" element={<AssistantPage />} />}
                            <Route path="/worker" element={<WorkerPage />} />
                            <Route path="/feed-inventory" element={<Protected perm="manage_inventory"><FeedInventoryPage /></Protected>} />
                            <Route path="/purchasing" element={<Protected perm="manage_inventory"><PurchasingPage /></Protected>} />
                            <Route path="/sales" element={<Protected perm="add"><SalesPage /></Protected>} />
                            <Route path="/inventory" element={<Protected perm="manage_inventory"><InventoryPage /></Protected>} />
                            <Route path="/sales-inventory" element={<Protected perm="manage_inventory"><SalesInventoryPage /></Protected>} />
                            <Route path="/pos" element={<Protected perm="add"><PosPage /></Protected>} />
                            <Route path="/customers" element={<Protected perm="add"><CustomersPage /></Protected>} />
                            <Route path="/shifts" element={<Protected perm="add"><ShiftsPage /></Protected>} />
                            <Route path="/deliveries" element={<Protected perm="add"><DeliveriesPage /></Protected>} />
                            <Route path="/growth" element={<Protected perm="add"><GrowthPage /></Protected>} />
                            <Route path="/sales-dashboard" element={<Protected perm="view"><SalesDashboardPage /></Protected>} />
                            <Route path="/batches" element={<Protected perm="manage_inventory"><BatchesPage /></Protected>} />
                            <Route path="/construction" element={<Protected perm="manage_settings"><ConstructionPage /></Protected>} />
                            <Route path="/birth-to-sale" element={<Protected perm="view"><BirthToSalePage /></Protected>} />
                            <Route path="/crops" element={<Protected perm="view"><CropsPage /></Protected>} />
                            <Route path="/hr" element={<Protected perm="manage_users"><HRPage /></Protected>} />
                            <Route path="/payroll" element={<Protected perm="manage_finance"><PayrollPage /></Protected>} />
                            <Route path="/aquaculture" element={<Protected perm="view"><AquaculturePage /></Protected>} />
                            <Route path="/animals/:aid" element={<Protected perm="view"><AnimalProfilePage /></Protected>} />
                            <Route path="/alerts" element={<Protected perm="view"><AlertsPage /></Protected>} />
                            <Route path="/notifications" element={<Protected perm="view"><NotificationsPage /></Protected>} />
                            <Route path="/admin" element={<Protected perm="manage_settings"><AdminSettingsPage /></Protected>} />
                            <Route path="/forms" element={<Protected perm="view"><FormsPage /></Protected>} />
                            <Route path="/fleet" element={<Protected perm="manage_settings"><FleetPage /></Protected>} />
                            <Route path="/equipment" element={<Protected perm="manage_settings"><EquipmentPage /></Protected>} />
                            <Route path="/admin/users" element={<Protected owner><AdminUsersPage /></Protected>} />
                            <Route path="/admin/sync" element={<Protected owner><SyncMonitorPage /></Protected>} />
                            <Route path="/admin/recycle-bin" element={<Protected strictOwner><RecycleBinPage /></Protected>} />
                            <Route path="/incidents" element={<Protected perm="view"><IncidentsPage /></Protected>} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </ErrorBoundary>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

function RoleLanding() {
    const { user } = useAuth();
    return <Navigate to={getHomeRouteForUser(user)} replace />;
}

function FieldModeRoute() {
    const { user } = useAuth();
    if (!canEnterFieldMode(user)) return <AccessDenied />;
    const route = getFieldModeRouteForUser(user);
    return isDashboardUser(user) ? <Navigate to={route} replace /> : <EmployeeShell />;
}

function FieldModeDashboardRoute() {
    const { user } = useAuth();
    return canEnterFieldMode(user) ? <FieldModePage /> : <AccessDenied />;
}

function AccessDenied() {
    const { t } = useTranslation();
    return (
        <div className="p-8">
            <h1 className="text-2xl font-bold">{t("access.denied")}</h1>
            <p className="text-muted-foreground mt-2">{t("access.field_restricted")}</p>
        </div>
    );
}
